// src/app/time-attendance/scheduling/pages/scheduling-sync/scheduling-sync.component.ts

import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { Subject, firstValueFrom, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { SchedulingNavComponent } from '../../../shared/scheduling-nav/scheduling-nav.component';
import { SchedulingSyncService } from '../../services/scheduling-sync.service';
import {
  ApiPagedResponse,
  ScheduleSyncProgress,
  YywwListItem,
} from '../../models/scheduling-sync.model';

type ToastKind = 'success' | 'error' | 'info';

@Component({
  selector: 'app-scheduling-sync',
  standalone: true,
  imports: [CommonModule, FormsModule, SchedulingNavComponent, RouterModule],
  templateUrl: './scheduling-sync.component.html',
  styleUrls: ['./scheduling-sync.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchedulingSyncComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  isLoading = true;
  errorMessage: string | null = null;
  isSyncing = false;

  toastMessage: string | null = null;
  toastKind: ToastKind = 'info';
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  selectedYyww: YywwListItem | null = null;

  yywwOpen = false;
  yywwSearch = String(new Date().getFullYear());
  yywwResults: YywwListItem[] = [];
  yywwIsLoading = false;

  private yywwPage = 1;
  private readonly yywwPageSize = 20;
  private yywwHasMore = true;
  private yywwActiveTerm = '';
  private yywwSeq = 0;

  private readonly yywwSearch$ = new Subject<string>();

  syncState: ScheduleSyncProgress | null = null;

  private hasSeenFirstSyncState = false;
  private lastSyncStatus: ScheduleSyncProgress['status'] | null = null;

  constructor(
    private readonly syncSvc: SchedulingSyncService,
    private readonly cdr: ChangeDetectorRef,
    private readonly hostEl: ElementRef<HTMLElement>,
  ) { }

  ngOnInit(): void {
    this.syncSvc.syncState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.syncState = state ?? null;
        this.isSyncing = state?.status === 'running';

        const status = state?.status ?? null;

        if (!this.hasSeenFirstSyncState) {
          this.hasSeenFirstSyncState = true;
          this.lastSyncStatus = status;
          this.cdr.markForCheck();
          return;
        }

        const changed = status !== this.lastSyncStatus;
        this.lastSyncStatus = status;

        if (changed && status === 'success') {
          this.showToast('success', 'Schedule sync completed.');

          this.resetForm();
        } else if (changed && status === 'error') {
          this.showToast('error', this.humanizeSyncError(state?.lastError));
        }

        this.cdr.markForCheck();
      });

    this.yywwSearch$
      .pipe(
        takeUntil(this.destroy$),
        map((value) => (value ?? '').trim()),
        debounceTime(1000),
        distinctUntilChanged(),
      )
      .subscribe((term) => {
        void this.yywwResetAndSearch(term);
      });

    void this.loadCore();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearToast();
  }

  @HostListener('document:mousedown', ['$event'])
  onDocMouseDown(event: MouseEvent): void {
    if (!this.yywwOpen) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const host = this.hostEl.nativeElement;
    if (!host.contains(target)) {
      this.closeAllDropdowns();
      return;
    }

    const insidePanel = !!target.closest('.combo-panel');
    const insideTrigger = !!target.closest('.combo-input');

    if (!insidePanel && !insideTrigger) {
      this.closeAllDropdowns();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.yywwOpen) {
      this.closeAllDropdowns();
    }
  }

  async loadCore(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = null;
    this.cdr.markForCheck();

    try {
      await this.yywwResetAndSearch(this.yywwSearch.trim());
    } catch (error) {
      console.error(error);
      this.errorMessage = 'Failed to load weeks.';
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  refresh(): void {
    void this.loadCore();
  }

  private resetForm(): void {
    this.selectedYyww = null;

    // reset search back to current year (clean UX)
    this.yywwSearch = String(new Date().getFullYear());

    // reload list so dropdown is fresh
    void this.yywwResetAndSearch(this.yywwSearch.trim());

    this.yywwOpen = false;

    this.cdr.markForCheck();
  }

  closeAllDropdowns(): void {
    this.yywwOpen = false;
    this.cdr.markForCheck();
  }

  openYyww(): void {
    this.closeAllDropdowns();
    this.yywwOpen = true;
    this.cdr.markForCheck();
  }

  onYywwSearchChange(raw: string): void {
    const value = raw ?? '';
    this.yywwSearch = value;
    this.yywwSearch$.next(value);
    this.cdr.markForCheck();
  }

  private async yywwResetAndSearch(term: string): Promise<void> {
    const activeSeq = ++this.yywwSeq;

    this.yywwActiveTerm = term;
    this.yywwPage = 1;
    this.yywwHasMore = true;
    this.yywwResults = [];
    this.yywwIsLoading = false;
    this.errorMessage = null;
    this.cdr.markForCheck();

    if (!term) {
      this.yywwHasMore = false;
      this.cdr.markForCheck();
      return;
    }

    await this.yywwLoadNextPage(activeSeq);
  }

  async yywwLoadNextPage(seq: number = this.yywwSeq): Promise<void> {
    if (seq !== this.yywwSeq) {
      return;
    }

    const term = this.yywwActiveTerm.trim();
    if (!term || this.yywwIsLoading || !this.yywwHasMore) {
      return;
    }

    this.yywwIsLoading = true;
    this.cdr.markForCheck();

    try {
      const response = await firstValueFrom(
        this.syncSvc
          .searchYywws({
            searchTerm: term,
            pageNumber: this.yywwPage,
            pageSize: this.yywwPageSize,
          })
          .pipe(
            catchError((error) => {
              console.error('searchYywws error', error);

              const empty: ApiPagedResponse<YywwListItem> = {
                status: 'error',
                totalCount: 0,
                totalPages: 1,
                currentPage: 1,
                pageSize: this.yywwPageSize,
                data: [],
              };

              return of(empty);
            }),
          ),
      );

      if (seq !== this.yywwSeq) {
        return;
      }

      const pageData = response?.data ?? [];
      const totalPages = Number(response?.totalPages ?? 1) || 1;

      this.yywwResults = [...this.yywwResults, ...pageData];
      this.yywwHasMore = this.yywwPage < totalPages && pageData.length > 0;

      if (this.yywwHasMore) {
        this.yywwPage += 1;
      }
    } finally {
      if (seq === this.yywwSeq) {
        this.yywwIsLoading = false;
        this.cdr.markForCheck();
      }
    }
  }

  onYywwScroll(event: Event): void {
    const element = event.target as HTMLElement | null;
    if (!element) {
      return;
    }

    const nearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 120;
    if (nearBottom) {
      void this.yywwLoadNextPage();
    }
  }

  selectWeekFromDropdown(week: YywwListItem): void {
    this.selectedYyww = week;
    this.yywwOpen = false;
    this.cdr.markForCheck();
  }

  clearSelectedWeek(): void {
    this.selectedYyww = null;
    this.yywwSearch = String(new Date().getFullYear());
    this.yywwOpen = false;
    void this.yywwResetAndSearch(this.yywwSearch.trim());
    this.cdr.markForCheck();
  }

  get canSync(): boolean {
    const weekSelected = !!this.selectedYyww?.id;
    const running = this.syncState?.status === 'running';
    return weekSelected && !running;
  }

  startSync(): void {
    const yywwId = this.selectedYyww?.id ?? null;

    if (!yywwId) {
      this.showToast('error', 'Select a week.');
      return;
    }

    this.showToast('info', 'Sync started…');
    this.syncSvc.startSync({ yywwId });
    this.cdr.markForCheck();
  }

  get selectedWeekSummary(): string {
    if (!this.selectedYyww) {
      return '—';
    }

    const fullYearWeek = this.selectedYyww.fullYearWeek ?? this.selectedYyww.yearWeek ?? '';
    const range =
      this.selectedYyww.startDate && this.selectedYyww.endDate
        ? `${this.selectedYyww.startDate} → ${this.selectedYyww.endDate}`
        : '';

    return `${fullYearWeek}${range ? ` (${range})` : ''}`.trim();
  }

  clearToast(): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }

    this.toastTimer = null;
    this.toastMessage = null;
    this.cdr.markForCheck();
  }

  private showToast(kind: ToastKind, message: string): void {
    this.toastKind = kind;
    this.toastMessage = message;

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }

    this.toastTimer = setTimeout(() => this.clearToast(), 2600);
    this.cdr.markForCheck();
  }

  private humanizeSyncError(raw?: string | null): string {
    const message = (raw ?? '').trim();
    if (!message) {
      return 'Schedule sync failed.';
    }

    return message;
  }

  trackById(_: number, item: { id: number }): number {
    return item.id;
  }
}